import * as React from 'react'
import { Link } from 'react-router-dom'
import { graphHighlightHref } from '@atbash-labs/plantview-links'
import type { NodeLabel } from '@atbash-labs/ontology-core'
import {
  collectAdditionalProperties,
  useGetPlantVendorDetailQuery,
  usePlantSessionPaint,
  type PlantEquipment,
  type PlantVendorPart
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { PlantHref } from '../PlantHref'
import { registryIconFor } from '../registry-icons'
import { SessionEntityMark, SessionTitleMark, sessionDetailHeaderClass } from '../session-mark-ui'
import { EntityDetailsInspector } from './EntityDetailsInspector'
import { partSpareBadge } from './part-detail-helpers'
import { PlantStrayPill } from './PlantStrayPill'
import { SectionHeader } from './SectionHeader'

export function VendorEntityPage({
  entityId
}: {
  entityId: string
  label: NodeLabel
}): React.ReactElement {
  const { data, isLoading, isError } = useGetPlantVendorDetailQuery(entityId, {
    skip: !entityId
  })
  const { markFor } = usePlantSessionPaint()

  if (isLoading) {
    return <div className="rounded-xl border bg-card p-8 text-sm text-ink-3">Loading vendor…</div>
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border bg-card p-8 text-card-foreground shadow-sm">
        <h1 className="m-0 text-xl font-semibold">Vendor not found</h1>
        <p className="mt-2 break-all font-mono text-xs text-ink-3">{entityId}</p>
        <Button asChild variant="outline" className="mt-5">
          <Link to={graphHighlightHref(entityId)}>Open graph view</Link>
        </Button>
      </div>
    )
  }

  const { vendor, parts, equipment } = data
  const mark = markFor(vendor.id)
  const Icon = registryIconFor('Vendor')
  const additionalProperties = collectAdditionalProperties(
    'Vendor',
    vendor as unknown as Record<string, unknown>
  )

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <header
        className={sessionDetailHeaderClass(
          'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
          mark
        )}
      >
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
              {vendor.name}
            </h1>
            <SessionTitleMark mark={mark} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="outline">Vendor</Badge>
            {vendor.vendor_kind && <Badge variant="secondary">{vendor.vendor_kind}</Badge>}
            {vendor.vendor_code && <Badge variant="outline">{vendor.vendor_code}</Badge>}
            <PlantStrayPill isStray={data.isStray} />
          </div>
          {vendor.contact && <div className="mt-1.5 text-13 text-ink-3">{vendor.contact}</div>}
          <div className="mt-1.5 break-all font-mono text-11 text-ink-4">{vendor.id}</div>
        </div>
      </header>

      <PartsRoleSection
        label="Parts manufactured"
        parts={parts.filter((row) => row.role === 'manufacturer')}
      />
      <PartsRoleSection
        label="Parts supplied"
        parts={parts.filter((row) => row.role === 'supplier')}
      />
      <EquipmentSection equipment={equipment} />
      <EntityDetailsInspector entity={vendor} additionalProperties={additionalProperties} />
    </div>
  )
}

function PartsRoleSection({
  label,
  parts
}: {
  label: string
  parts: PlantVendorPart[]
}): React.ReactElement | null {
  if (parts.length === 0) return null
  return (
    <section>
      <SectionHeader
        label={label}
        subtitle={`${parts.length} ${parts.length === 1 ? 'part' : 'parts'}`}
      />
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-12">
            <thead>
              <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
                <th className="px-3 py-1.5 font-semibold">Part</th>
                <th className="px-3 py-1.5 font-semibold">Customer P/N</th>
                <th className="px-3 py-1.5 font-semibold">Mfr P/N</th>
                <th className="px-3 py-1.5 font-semibold">Type</th>
                <th className="px-3 py-1.5 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody>
              {parts.map(({ part, role }) => {
                const spareBadge = partSpareBadge(part)
                return (
                  <tr key={`${part.id}:${role}`} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <PlantHref
                          id={part.id}
                          label="Part"
                          node={part}
                          className="min-w-0 truncate font-medium text-ink no-underline hover:text-brand hover:underline"
                        >
                          {part.name}
                        </PlantHref>
                        <SessionEntityMark entityId={part.id} />
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-ink-3">
                      {part.customer_pn?.trim() || '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-ink-3">
                      {part.manufacturer_pn?.trim() || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-ink-3">{part.part_type?.trim() || '—'}</td>
                    <td className="px-3 py-1.5">
                      {spareBadge ? (
                        <Badge
                          variant="outline"
                          className={`px-1.5 py-0 text-10 ${spareBadge.className}`}
                        >
                          {spareBadge.label}
                        </Badge>
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function EquipmentSection({
  equipment
}: {
  equipment: PlantEquipment[]
}): React.ReactElement | null {
  if (equipment.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Reachable equipment"
        subtitle={`${equipment.length} ${equipment.length === 1 ? 'asset' : 'assets'}`}
      />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {equipment.map((item) => (
          <PlantHref
            key={item.id}
            id={item.id}
            label="Equipment"
            node={item}
            className="inline-flex items-center gap-1.5 rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
          >
            {item.name}
            <SessionEntityMark entityId={item.id} />
          </PlantHref>
        ))}
      </div>
    </section>
  )
}
